import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { http } from "@/lib/api";

// Lote 139 (Robson, 28/10/2026) — "Pergunte à sua agenda": uma lupa no Painel para perguntas curtas
// ("Qual minha próxima audiência?", "Prazos este mês", "Quantos processos o cliente Fulano tem?").
// Etapa 1: respostas prontas, calculadas no servidor por regras (sem IA, sem custo, nada sai do app).
// Dois toques: tocar num exemplo já pergunta; ou digitar + Enter. A resposta vem em uma frase,
// com até 5 itens que levam direto à tela certa.
export const EXEMPLOS = [
  "Minha próxima audiência",
  "Audiências da próxima semana",
  "Prazos este mês",
  "Quantos processos eu tenho?",
];

// Lote 153 — na gaveta (gaveta=true) a pergunta vai para POST /assistente/perguntar: o servidor tenta as regras primeiro
// (grátis) e só usa a IA quando elas não entendem, ou no resumo de um processo (processoId). Resposta traz `origem`
// ("regras" | "ia"), `restantes` (perguntas com IA que sobram hoje) e as fontes (links dos registros usados).
export default function PerguntaAgenda({ exemplos = EXEMPLOS, gaveta = false, aoNavegar = null, contexto = null, tela = null, pedido = null, aoConsumirPedido = null }) {
  const [q, setQ] = useState("");
  const [resp, setResp] = useState(null);      // {ok, answer, items, more, suggestions}
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [historico, setHistorico] = useState([]);   // Lote 154: últimas trocas [{p, r}] para perguntas de continuação
  const [ultima, setUltima] = useState("");          // pergunta que gerou a resposta mostrada
  const processoId = contexto?.tipo === "processo" ? contexto.id : null;
  const audienciaId = contexto?.tipo === "audiencia" ? contexto.id : null;
  const seq = useRef(0);                         // ignora respostas atrasadas de perguntas antigas
  const inputRef = useRef(null);

  const perguntar = async (texto, { semTexto = false } = {}) => {
    const t = (texto ?? q).trim();
    if (!t && !semTexto) return;
    setQ(t);
    const meu = ++seq.current;
    setCarregando(true);
    setErro(false);
    try {
      const r = gaveta
        ? await http.post("/assistente/perguntar", {
          q: t, processo_id: processoId || undefined, audiencia_id: audienciaId || undefined, tela: tela || undefined,
          historico: historico.length ? historico.slice(-3) : undefined,
        })
        : await http.get("/pergunta", { params: { q: t } });
      if (meu === seq.current) {
        setResp(r.data);
        setUltima(t);
        const util = r.data?.ok !== false && (r.data?.origem === "ia" || r.data?.origem === "regras")
          && !["limite", "ia_falhou", "vazio", "ia_desligada", "segredo", "nao_entendi"].includes(r.data?.intent) && r.data?.answer;
        if (gaveta && util) setHistorico((h) => [...h, { p: t || "Resumo automático", r: String(r.data.answer).slice(0, 700) }].slice(-3));
      }
    } catch {
      if (meu === seq.current) { setErro(true); setResp(null); }
    } finally {
      if (meu === seq.current) setCarregando(false);
    }
  };

  // Botão "Resumir movimentações" no processo: a gaveta abre e já faz a pergunta (uma vez por clique).
  const ultimoPedido = useRef(0);
  useEffect(() => {
    if (gaveta && processoId && pedido && pedido.n !== ultimoPedido.current) {
      ultimoPedido.current = pedido.n;
      perguntar(pedido.texto || "", { semTexto: true });
      if (aoConsumirPedido) aoConsumirPedido();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.n, processoId]);

  const limpar = () => {
    seq.current += 1;
    setQ(""); setResp(null); setErro(false); setCarregando(false); setHistorico([]); setUltima("");
    inputRef.current?.focus();
  };

  // Fecha só a resposta (o texto digitado e os exemplos continuam); o X do campo limpa tudo.
  const fechar = () => { seq.current += 1; setResp(null); setErro(false); setCarregando(false); setHistorico([]); setUltima(""); };

  const chips = resp && !resp.ok && resp.suggestions?.length ? resp.suggestions : exemplos;

  return (
    <section aria-label="Pergunte à sua agenda" data-testid="pergunta-agenda">
      <form
        onKeyDown={(e) => { if (e.key === "Escape" && resp) { e.stopPropagation(); fechar(); } }}
        onSubmit={(e) => { e.preventDefault(); perguntar(); }}
        className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 min-h-[48px] focus-within:border-stone-500"
      >
        <Search className="w-5 h-5 shrink-0 text-stone-500" strokeWidth={1.75} aria-hidden="true" />
        <input
          ref={inputRef}
          data-testid="pergunta-input"
          type="text"
          inputMode="search"
          enterKeyHint="search"
          value={q}
          maxLength={gaveta ? 400 : 200}
          onChange={(e) => setQ(e.target.value)}
          placeholder={gaveta ? (historico.length ? "Continue a conversa…" : audienciaId ? "Sobre esta audiência…" : processoId ? "Sobre este processo…" : "Pergunte à sua agenda…") : "Pergunte à sua agenda…"}
          aria-label="Pergunte à sua agenda"
          className="flex-1 min-w-0 bg-transparent text-base text-stone-900 placeholder:text-stone-500 outline-none py-2"
        />
        {q && (
          <button type="button" onClick={limpar} data-testid="pergunta-limpar" aria-label="Limpar pergunta"
            className="w-9 h-9 inline-flex items-center justify-center rounded text-stone-500 hover:text-stone-800">
            <X className="w-4 h-4" />
          </button>
        )}
        <button type="submit" data-testid="pergunta-enviar" disabled={!q.trim() || carregando}
          className="shrink-0 inline-flex items-center gap-1 rounded-md bg-stone-900 text-white text-sm font-semibold px-3 min-h-[36px] disabled:opacity-40">
          Perguntar <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      {gaveta ? (
        <div className="flex flex-col gap-1.5 mt-3" data-testid="pergunta-exemplos">
          <span className="text-xs text-stone-500" data-testid="pergunta-exemplos-rotulo">Experimente:</span>
          {chips.map((ex) => (
            <button key={ex} type="button" onClick={() => perguntar(ex)} disabled={carregando} data-testid="pergunta-exemplo"
              className="text-left text-sm rounded-lg border border-stone-200 hover:bg-stone-50 text-stone-700 px-3 py-2 min-h-[40px]">
              {ex}
            </button>
          ))}
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-x-1.5 md:gap-x-2 gap-y-0.5 md:gap-y-1 mt-2" data-testid="pergunta-exemplos">
          <span className="text-[10px] md:text-xs text-stone-400 md:text-stone-500" data-testid="pergunta-exemplos-rotulo">Exemplos:</span>
          {chips.map((ex, i) => (
            <button key={ex} type="button" onClick={() => perguntar(ex)} disabled={carregando}
              className={`${i >= 2 ? "hidden md:inline-flex " : ""}items-center text-[10px] md:text-xs rounded-full border border-stone-100 md:border-stone-200 bg-transparent hover:bg-stone-50 text-stone-400 md:text-stone-500 hover:text-stone-700 px-1.5 md:px-2.5 min-h-[32px]`}>
              {ex}
            </button>
          ))}
        </div>
      )}

      <div aria-live="polite" data-testid="pergunta-area">
        {carregando && <p className="mt-3 text-sm text-stone-600" data-testid="pergunta-carregando">{gaveta ? "Consultando…" : "Procurando…"}</p>}
        {erro && !carregando && (
          <p className="mt-3 text-sm text-red-700" data-testid="pergunta-erro">Não consegui responder agora. Tente de novo em instantes.</p>
        )}
        {resp && !carregando && (
          <div className="relative mt-3 rounded-lg border border-stone-200 bg-white p-3.5 pr-11" data-testid="pergunta-resposta" data-intent={resp.intent} data-ok={resp.ok ? "1" : "0"}>
            <button type="button" onClick={fechar} data-testid="pergunta-fechar" aria-label="Fechar resposta"
              className="absolute top-1.5 right-1.5 w-9 h-9 inline-flex items-center justify-center rounded text-stone-400 hover:text-stone-700 hover:bg-stone-100">
              <X className="w-4 h-4" />
            </button>
            {gaveta && ultima && (
              <p className="mb-1.5 text-xs text-stone-500 break-words" data-testid="pergunta-ultima">Você perguntou: “{ultima}”</p>
            )}
            {gaveta && resp.origem === "ia" && (
              <span className="inline-flex items-center gap-1 mb-1.5 rounded-full bg-stone-100 text-stone-600 text-[11px] font-semibold px-2 py-0.5" data-testid="pergunta-selo-ia">
                <Sparkles className="w-3 h-3" strokeWidth={2} /> Resposta da IA
              </span>
            )}
            <p className={resp.origem === "ia" ? "text-sm text-stone-900 leading-relaxed whitespace-pre-line" : "text-base font-semibold text-stone-900 leading-snug"} data-testid="pergunta-texto">{resp.answer}</p>
            {resp.origem === "ia" && resp.items?.length > 0 && <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500" data-testid="pergunta-fontes-rotulo">Fontes consultadas</p>}
            {resp.items?.length > 0 && (
              <ul className="mt-2 divide-y divide-stone-100" data-testid="pergunta-itens">
                {resp.items.map((it, i) => (
                  <li key={`${it.to}-${i}`}>
                    <Link to={it.to} data-testid="pergunta-item" onClick={aoNavegar || undefined}
                      className="flex items-center gap-2 py-2.5 min-h-[48px] text-left hover:bg-stone-50 rounded">
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-stone-900">{it.label}</span>
                        {it.sub && <span className="block text-sm text-stone-600 break-words">{it.sub}</span>}
                      </span>
                      <ChevronRight className="w-4 h-4 shrink-0 text-stone-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {gaveta && resp.origem === "ia" && (
              <p className="mt-2 text-[11px] text-stone-500" data-testid="pergunta-restantes">
                Confira os dados nos registros acima antes de agir. {resp.restantes} {resp.restantes === 1 ? "pergunta com IA restante" : "perguntas com IA restantes"} hoje.
              </p>
            )}
            {gaveta && historico.length > 0 && (
              <p className="mt-2 text-[11px] text-stone-500" data-testid="pergunta-continuar">
                Pode fazer uma pergunta de continuação (ex.: “e o prazo dele?”). <button type="button" onClick={limpar} data-testid="pergunta-nova-conversa" className="underline underline-offset-2 font-semibold text-stone-700">Nova conversa</button>
              </p>
            )}
            {resp.more ? (
              <Link to={resp.more.to} data-testid="pergunta-mais" onClick={aoNavegar || undefined}
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-stone-800 underline underline-offset-2 min-h-[36px]">
                {resp.more.label} <ChevronRight className="w-4 h-4" />
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
